import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Heart, TrendingUp, Target, CheckCircle2, Calendar, Zap, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import personalizedPlanImage from "@/assets/personalized-plan-journal.jpg";

interface UserProfile {
  journey_stage?: string;
  symptoms?: string[];
  goals?: string[];
  knowledge_level?: string;
}

export const PersonalizedPlanCard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    checkUserAndProfile();
  }, []);

  const checkUserAndProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setIsLoggedIn(true);
      const { data } = await supabase
        .from("profiles")
        .select("journey_stage, symptoms, goals, knowledge_level")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (data) {
        setProfile(data);
      }
    }
  };

  const getJourneyProgress = () => {
    if (!profile?.journey_stage) return 25;
    const stages = { 'pre-perimenopause': 25, 'perimenopause': 50, 'menopause': 75, 'post-menopause': 100 };
    return stages[profile.journey_stage as keyof typeof stages] || 25;
  };

  const getRecommendations = () => {
    if (!profile) return defaultRecommendations;
    
    const symptoms = profile.symptoms || [];
    const recs = [];

    if (symptoms.includes('hot_flashes')) {
      recs.push({ icon: Zap, title: "Cool Down Practice", desc: "Try 4-7-8 breathing", path: "/sos-toolkit" });
    }
    if (symptoms.includes('sleep_issues')) {
      recs.push({ icon: Calendar, title: "Sleep Protocol", desc: "Evening wind-down routine", path: "/sos-toolkit" });
    }
    if (symptoms.includes('mood_changes')) {
      recs.push({ icon: Heart, title: "Mood Support", desc: "Grounding exercises", path: "/sos-toolkit" });
    }

    return recs.length > 0 ? recs.slice(0, 3) : defaultRecommendations;
  };

  const defaultRecommendations = [
    { icon: Target, title: "Start Tracking", desc: "Log your first symptoms", path: "/tracker" },
    { icon: TrendingUp, title: "Quick Learn", desc: "2-min hormone lesson", path: "/micro-learning" },
    { icon: Heart, title: "Self-Care", desc: "5-min breathing exercise", path: "/sos-toolkit" },
  ];

  const recommendations = getRecommendations();

  if (!isLoggedIn) {
    return (
      <Card 
        className="relative p-8 hover:shadow-lifted hover:scale-[1.02] transition-all duration-300 border-2 hover:border-primary/70 animate-fade-up overflow-hidden cursor-pointer" 
        style={{ 
          animationDelay: '0.4s',
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${personalizedPlanImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
            <Heart className="h-7 w-7 text-white" />
          </div>
          <h3 className="text-2xl font-semibold mb-3 text-white">Your Personalized Plan</h3>
          <p className="text-white/90 leading-relaxed mb-4">
            Daily protocols tailored to your unique journey. Small steps, lasting change.
          </p>
          <Button 
            onClick={() => navigate("/auth")} 
            className="w-full bg-white text-primary hover:bg-white/90"
            variant="outline"
          >
            Sign Up to Get Started
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      className="relative p-8 hover:shadow-lifted hover:scale-[1.02] hover:border-primary/50 transition-all duration-300 border-2 border-primary/30 animate-fade-up overflow-hidden cursor-pointer" 
      style={{ 
        animationDelay: '0.4s',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${personalizedPlanImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left relative z-10"
      >
        <div className="flex items-start justify-between mb-6">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Heart className="h-7 w-7 text-white" />
          </div>
          <div className="flex items-center gap-2">
            {profile?.journey_stage && (
              <Badge variant="secondary" className="capitalize bg-white/20 text-white border-white/30">
                {profile.journey_stage.replace('-', ' ')}
              </Badge>
            )}
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-white" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white" />
            )}
          </div>
        </div>

        <h3 className="text-2xl font-semibold mb-2 text-white">Your Personalized Plan</h3>
        <p className="text-sm text-white/90 mb-6">
          {isExpanded ? "Daily guidance tailored to your journey" : "Click to view your personalized wellness plan"}
        </p>
      </button>

      {isExpanded && (
        <div className="space-y-6 animate-fade-in relative z-10">
          {/* Journey Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">Journey Progress</span>
              <span className="text-sm text-white/80">{getJourneyProgress()}%</span>
            </div>
            <Progress value={getJourneyProgress()} className="h-2" />
          </div>

          {/* Today's Recommendations */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-white" />
              Recommended For You
            </h4>
            {recommendations.map((rec, idx) => {
              const Icon = rec.icon;
              return (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(rec.path);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-colors text-left group"
                >
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{rec.title}</p>
                    <p className="text-xs text-white/80 truncate">{rec.desc}</p>
                  </div>
                  <CheckCircle2 className="h-5 w-5 text-white/30 group-hover:text-white transition-colors" />
                </button>
              );
            })}
          </div>

          {/* View Full Plan */}
          <Button 
            onClick={(e) => {
              e.stopPropagation();
              navigate("/plan");
            }}
            className="w-full bg-white text-primary hover:bg-white/90"
            variant="default"
          >
            View Full Plan
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </Card>
  );
};
